<template>
  <super-admin-menu-layout>
    <h2 class="mb-4">{{ $t('superadmin_menu.slow_queries') }}</h2>
    <div class="d-flex flex-wrap ga-2 mb-2 align-center">
      <v-text-field
        class="filter-field"
        :label="$t('superadmin.search')"
        prepend-inner-icon="mdi-magnify"
        v-model="filter"
        clearable
        hide-details
        density="compact"
      />
      <v-select
        class="release-field"
        :label="$t('superadmin.release')"
        :items="releaseOptions"
        item-title="title"
        item-value="value"
        v-model="releaseVersion"
        clearable
        hide-details
        density="compact"
        data-testid="slow-log-release-filter"
      />
    </div>
    <div class="d-flex flex-wrap ga-2 mb-3 align-center">
      <v-btn
        color="primary"
        variant="outlined"
        size="small"
        :disabled="loading || totalItems === 0"
        @click="exportCsv"
      >
        {{ $t('superadmin.export_csv') }}
      </v-btn>
      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn
            color="error"
            variant="outlined"
            size="small"
            v-bind="props"
            :disabled="loading || deleting"
            data-testid="slow-log-delete-menu"
          >
            {{ $t('superadmin.delete_entries') }}
          </v-btn>
        </template>
        <v-list density="compact">
          <v-list-item
            data-testid="slow-log-delete-release"
            :disabled="!releaseVersion"
            @click="confirmDelete('release')"
          >
            <v-list-item-title>{{ $t('superadmin.delete_this_release') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            data-testid="slow-log-delete-other"
            :disabled="!releaseVersion"
            @click="confirmDelete('other')"
          >
            <v-list-item-title>{{ $t('superadmin.delete_other_releases') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            data-testid="slow-log-delete-all"
            @click="confirmDelete('all')"
          >
            <v-list-item-title>{{ $t('superadmin.delete_all_entries') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <span v-if="currentRelease" class="text-medium-emphasis text-body-2" data-testid="slow-log-current-release">
        {{ $t('superadmin.current_release') }}: {{ currentRelease }}
      </span>
    </div>
    <v-data-table-server
      v-model:page="options.page"
      v-model:items-per-page="options.itemsPerPage"
      v-model:sort-by="options.sortBy"
      :loading="loading"
      :items="items"
      :items-length="totalItems"
      :headers="headers"
      item-value="id"
      @update:options="onOptionsUpdate"
    >
      <template v-slot:item.release_version="{ item }">
        <span data-testid="slow-log-row-release">{{ item.release_version || '—' }}</span>
      </template>
      <template v-slot:item.sql="{ item }">
        <span class="sql-cell" :title="item.sql">{{ truncate(item.sql, 160) }}</span>
      </template>
    </v-data-table-server>
  </super-admin-menu-layout>
</template>

<script>
import SuperAdminMenuLayout from "@/components/layout/SuperAdminMenuLayout.vue";
import { downloadTextFile } from "@/utils/labelCsv";

export default {
  name: "SuperAdminSlowQueries",
  components: { SuperAdminMenuLayout },
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: [{ key: 'created_at', order: 'desc' }],
      },
      items: [],
      totalItems: 0,
      loading: true,
      deleting: false,
      filter: '',
      releaseVersion: null,
      releases: [],
      currentRelease: '',
    };
  },
  computed: {
    headers() {
      return [
        { title: 'ID', key: 'id', sortable: true },
        { title: this.$t('superadmin.created_at'), key: 'created_at', sortable: true },
        { title: this.$t('superadmin.release'), key: 'release_version', sortable: true },
        { title: this.$t('superadmin.duration_ms'), key: 'duration_ms', sortable: true },
        { title: this.$t('superadmin.rows_affected'), key: 'rows_affected', sortable: true },
        { title: this.$t('superadmin.request_id'), key: 'request_id', sortable: true },
        { title: 'SQL', key: 'sql', sortable: false },
      ];
    },
    releaseOptions() {
      const opts = (this.releases || []).map((v) => ({ title: v, value: v }));
      if (this.currentRelease && !opts.some((o) => o.value === this.currentRelease)) {
        opts.unshift({ title: this.currentRelease, value: this.currentRelease });
      }
      return opts;
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.loadItems();
      },
    },
    filter() {
      this.options = { ...this.options, page: 1 };
      this.loadItems();
    },
    releaseVersion() {
      this.options = { ...this.options, page: 1 };
      this.loadItems();
    },
  },
  mounted() {
    this.loadItems();
  },
  methods: {
    onOptionsUpdate(value) {
      this.options = {
        page: 1,
        itemsPerPage: 25,
        sortBy: [{ key: 'created_at', order: 'desc' }],
        ...this.options,
        ...value,
      };
    },
    truncate(text, n) {
      const s = String(text || '');
      return s.length > n ? s.slice(0, n) + '…' : s;
    },
    async loadItems() {
      this.loading = true;
      const response = await this.$api.superadmin.slowQueries(
        this.options,
        this.filter,
        this.releaseVersion || '',
      );
      if (response && Array.isArray(response.items)) {
        this.items = response.items;
        this.totalItems = response.total != null ? response.total : response.items.length;
        if (Array.isArray(response.releases)) {
          this.releases = response.releases;
        }
        if (response.current_release) {
          this.currentRelease = response.current_release;
        }
      } else {
        this.items = [];
        this.totalItems = 0;
      }
      this.loading = false;
    },
    async exportCsv() {
      const response = await this.$api.superadmin.slowQueries(
        { ...this.options, page: 1, itemsPerPage: 10000 },
        this.filter,
        this.releaseVersion || '',
      );
      const rows = (response && response.items) || [];
      const lines = [
        ['id', 'created_at', 'release_version', 'duration_ms', 'rows_affected', 'request_id', 'sql'].join(','),
      ];
      for (const r of rows) {
        lines.push([
          r.id,
          r.created_at,
          r.release_version || '',
          r.duration_ms,
          r.rows_affected == null ? '' : r.rows_affected,
          r.request_id || '',
          `"${String(r.sql || '').replace(/"/g, '""')}"`,
        ].join(','));
      }
      downloadTextFile('slow-queries.csv', lines.join('\n'));
    },
    async confirmDelete(scope) {
      let message = this.$t('superadmin.delete_all_confirm');
      if (scope === 'release') {
        message = this.$t('superadmin.delete_release_confirm', { release: this.releaseVersion });
      } else if (scope === 'other') {
        message = this.$t('superadmin.delete_other_confirm', { release: this.releaseVersion });
      }
      if (!window.confirm(message)) {
        return;
      }
      this.deleting = true;
      try {
        await this.$api.superadmin.deleteSlowQueries(scope, this.releaseVersion || '');
        await this.loadItems();
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.filter-field {
  flex: 1 1 220px;
  max-width: 420px;
}
.release-field {
  flex: 0 1 200px;
  max-width: 240px;
}
.sql-cell {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.85em;
  white-space: nowrap;
}
</style>
